"use client";

import { definePatch } from "@web-kits/audio";
import type { PlayOptions, SoundPatch } from "@web-kits/audio";

import type { FeedbackEvent } from "./feedback-events";
import minimalPatch from "./minimal-patch.json" with { type: "json" };

/**
 * UI sound registry backed by the "Minimal" sound patch from the
 * `@web-kits/audio` registry (raphaelsalaja/audio), bundled locally so there is
 * no runtime fetch. The whole engine lives behind {@link playSound}, so the
 * patch can be swapped without touching components.
 *
 * `definePatch` builds an in-memory patch synchronously; the shared
 * `AudioContext` is still created lazily on first playback (gesture-gated by the
 * provider), so importing this module stays SSR-safe.
 */
const patch = definePatch(minimalPatch as SoundPatch);

/** Maps each semantic feedback event to a sound name in the Minimal patch. */
const EVENT_TO_SOUND: Record<FeedbackEvent, string> = {
  tap: "tap",
  select: "select",
  "toggle-on": "toggle-on",
  "toggle-off": "toggle-off",
  open: "expand",
  close: "collapse",
  success: "success",
  warn: "warning",
  error: "error",
  copy: "copy",
  delete: "delete",
  send: "send",
  undo: "undo",
  notify: "notification",
  info: "info",
};

/** Play the patch sound mapped to a feedback event. No-op if the sound is missing. */
export function playSound(event: FeedbackEvent, opts?: PlayOptions): void {
  const name = EVENT_TO_SOUND[event];

  if (!patch.get(name)) {
    return;
  }

  patch.play(name, opts);
}
